import { toast } from "sonner";
import { Plus, Trash2, Clock } from "lucide-react";
import { fmtCurrency } from "@/lib/format";

const inputCls = "w-full rounded-xl border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] py-2.5 px-3 text-sm text-[var(--hp-heading)] outline-none focus:border-[var(--hp-primary)] transition-colors duration-200";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] mb-1.5 block";

export function StaffHoursPicker({ rows, setRows, staff, idPrefix, label = "Staff on the floor", hoursLabel = "Hours" }) {
  const active = staff.filter((s) => s.active !== false);
  const addRow = () => {
    const used = new Set(rows.map((r) => r.staffId));
    const next = active.find((s) => !used.has(s.id)) || active[0];
    if (!next) { toast.error("Add staff in the Staff tab first"); return; }
    setRows([...rows, { staffId: next.id, hours: "" }]);
  };
  const update = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const rateOf = (id) => staff.find((s) => s.id === id)?.hourlyRate || 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className={labelCls + " !mb-0"}>{label}</label>
        <button type="button" data-testid={`${idPrefix}add-staff-row`} onClick={addRow}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--hp-profit)] hover:underline">
          <Plus className="h-3.5 w-3.5" /> Add staff
        </button>
      </div>
      {rows.length === 0 && (
        <p className="text-xs text-[var(--hp-muted)] rounded-xl border border-dashed border-[var(--hp-border)] px-3 py-2.5">
          {active.length ? "No staff assigned — click Add staff to pull base wages from hourly rates." : "No staff yet. Add team members in the Staff tab to auto-calculate wages."}
        </p>
      )}
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`${idPrefix}staff-row-${i}`}>
            <select data-testid={`${idPrefix}staff-select-${i}`} value={r.staffId} onChange={(e) => update(i, { staffId: e.target.value })}
              className={`${inputCls} min-w-[9rem] flex-1 basis-40`}>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}{s.active === false ? " (inactive)" : ""}</option>)}
            </select>
            <div className="relative w-28 shrink-0">
              <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--hp-muted)]" />
              <input data-testid={`${idPrefix}staff-hours-${i}`} type="number" step="0.25" min="0" value={r.hours} placeholder={hoursLabel}
                onChange={(e) => update(i, { hours: e.target.value })} className={`${inputCls} font-mono-num pl-8`} />
            </div>
            <span className="w-20 shrink-0 text-right font-mono-num text-xs text-[var(--hp-text)]">{fmtCurrency((parseFloat(r.hours) || 0) * rateOf(r.staffId))}</span>
            <button type="button" data-testid={`${idPrefix}staff-remove-${i}`} onClick={() => remove(i)}
              aria-label="Remove staff" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[var(--hp-muted)] hover:text-[var(--hp-loss)] hover:bg-[var(--hp-loss)]/10 transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
